import PriceGuideChart from '@c/dirt-bikes/PriceGuideChart';
import FormCounter from '@c/forms/controls/FormCounter';
import FormCurrencyInputFancy from '@c/forms/controls/FormCurrencyInputFancy';
import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import FormSelect, {
  generateSelectOptions,
} from '@c/forms/controls/FormSelect';
import { SaleFeature } from '@c/icons/sale';
import PageSection from '@c/listing/shared/PageSection';
import { useQuery } from '@tanstack/react-query';
import SafeImage from '@ui/SafeImage';
import Select from '@ui/Select';
import Toggle from '@ui/Toggle';
import { getNumber } from '@util/createListingHelpers';
import { getPublicCuratedLists } from '@util/firestore/curated/curated.service';
import { FEE } from '@util/firestore/listing/listing.types';
import { getPurchaseEntries } from '@util/firestore/purchase_entries';
import { getPublicUserDoc } from '@util/firestore/users';
import useDebounce from '@util/hooks/useDebounce';
import { formatCurrency } from '@util/index';
import { PurchaseEntryDocument } from '@util/types/firestore/purchase_entry';
import { BOOT_TRADE_IN_PURCHASE_ENTRY_ID } from 'app/dashboard/(dashboard)/purchase-entry/utils';
import { useAuth } from 'context/AuthContext';
import { isVehicleCategory } from 'models/product';
import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useCreateListing } from '../_context';
import { CheckItem } from './MediaSelect';

export const ProductPriceAndInventory = () => {
  const { userDoc } = useAuth();
  const { form, product, variant } = useCreateListing();
  const [variationSalePrice, setVariationSalePrice] = useState(product.price);
  const [boughtFrom, setBoughtFrom] = useState<string | null>(null);
  const debouncedBoughtFrom = useDebounce(boughtFrom, 500);
  const { data: user, refetch } = useQuery({
    queryKey: [
      'user',
      product.sources?.find((source) => source.collection === 'boot_trade_in')
        ?.id,
    ],
    queryFn: () =>
      product.sources?.find((source) => source.collection === 'boot_trade_in')
        ?.id
        ? getPublicUserDoc({
            uid: product.sources?.find(
              (source) => source.collection === 'boot_trade_in'
            )?.id,
          })
        : null,
  });
  useEffect(() => {
    if (user && boughtFrom === null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBoughtFrom(user.username);
    }
  }, [user, boughtFrom]);
  useEffect(() => {
    if (debouncedBoughtFrom) {
      getPublicUserDoc({
        username: debouncedBoughtFrom.trim(),
      })
        .then((user) => {
          if (!user) {
            form.setError('sources', {
              type: 'required',
              message: 'User not found',
            });
            return;
          }
          const sources = product.sources ?? [];
          const existingSource = sources.find(
            (source) => source.collection === 'boot_trade_in'
          );
          if (existingSource) {
            existingSource.id = user.uid;
          } else {
            sources.push({
              id: user.uid,
              collection: 'boot_trade_in',
            });
          }
          form.setValue('sources', sources);
          form.clearErrors('sources');
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }, [debouncedBoughtFrom, form, product.sources]);
  const { data: purchaseEntries } = useQuery({
    queryKey: ['purchaseEntries', userDoc?.uid],

    queryFn: async () => {
      if (!userDoc?.uid) return [];
      return (await getPurchaseEntries({ ownerUID: userDoc.uid })).results;
    },
    enabled:
      !!userDoc?.uid && !!(userDoc?.roles?.reseller || userDoc?.roles?.support),
  });
  const { data: curatedLists } = useQuery({
    queryKey: ['curated_lists'],
    queryFn: () => getPublicCuratedLists(),
  });
  const curatedListOptions =
    curatedLists?.map((e) => ({
      value: e.slug,
      id: e.slug,
      label: e.title,
    })) ?? [];
  const createPurchaseEntryLabels = (entries: PurchaseEntryDocument[]) => {
    let formattedObject: Record<string, string> = {};

    entries.forEach((entry) => {
      formattedObject[entry.id] = `${entry.title ? entry.title + ' ' : ''}${
        entry.id
      } (${entry.seller_bought_from.name}, ${new Date(
        entry.date_of_purchase
      ).toDateString()})`;
    });

    return formattedObject;
  };

  const purchaseEntryLabels = !!purchaseEntries
    ? createPurchaseEntryLabels(purchaseEntries)
    : {};

  if (
    product.is_auction &&
    !(userDoc?.roles?.reseller || userDoc?.roles?.support)
  )
    return null;
  return (
    <PageSection
      title="Price & Inventory"
      error={
        form.formState.errors.price?.message ||
        form.formState.errors.variations?.message
      }
    >
      <div className="flex justify-between gap-x-8">
        <div className="flex flex-col gap-4">
          {variant === 'edit-listing' && !product.is_auction && (
            <>
              {product.on_sale && !product.has_variations && (
                <div className="flex w-full flex-col items-start">
                  <span>
                    Original Price: {formatCurrency(product.og_price)}
                  </span>
                </div>
              )}
              <hr />
            </>
          )}

          <PriceGuideChart product={product} />

          {!product.is_auction && (
            <div className="py-8">
              <div
                className={`${
                  variant === 'edit-listing' &&
                  product.on_sale &&
                  !product.has_variations
                    ? 'flex gap-4'
                    : ''
                }`}
              >
                <FormLabel value="Price" required className="!text-[1.8rem]">
                  <Controller
                    control={form.control}
                    name={variant === 'edit-listing' ? 'og_price' : 'price'}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <div className="w-full md:max-w-[20rem]">
                        <FormCurrencyInputFancy
                          placeholder={'0.00'}
                          onChange={onChange}
                          onBlur={onBlur}
                          disabled={
                            variant === 'edit-listing' &&
                            product.on_sale &&
                            !product.has_variations
                          }
                          defaultValue={
                            variant === 'edit-listing'
                              ? product.og_price.toString()
                              : value.toString()
                          }
                        />
                        {/* net earnings */}
                        {!!product.price &&
                          !isVehicleCategory(product.category) &&
                          variant === 'create-listing' && (
                            <div className="ml-2 mt-[1rem] text-[1.4rem] text-brand-lightest-black">
                              <p>
                                Estimated Earnings:{' '}
                                {formatCurrency(product.price * (1 - FEE))}
                              </p>
                            </div>
                          )}
                      </div>
                    )}
                  />
                </FormLabel>
                {variant === 'edit-listing' &&
                  product.on_sale &&
                  !product.has_variations && (
                    <FormLabel value="Sale Price" className="!text-[1.8rem]">
                      <div className="max-w-[20rem]">
                        <FormCurrencyInputFancy
                          placeholder={'0.00'}
                          defaultValue={variationSalePrice.toString()}
                          onChange={(e) => {
                            form.setValue('price', getNumber(e.target.value));
                          }}
                        />
                      </div>
                    </FormLabel>
                  )}
              </div>
              {variant === 'edit-listing' &&
                product.on_sale &&
                !product.has_variations &&
                !!curatedLists?.length && (
                  <FormLabel value="Add to list?" className="mt-6">
                    <Select
                      onChange={(e) => {
                        if (e.id === 'empty') {
                          form.setValue('curated_lists', []);
                        } else {
                          form.setValue('curated_lists', [e.id]);
                        }
                      }}
                      options={curatedListOptions}
                      value={
                        curatedListOptions.find((c) =>
                          product.curated_lists?.includes(c.id)
                        )?.id ?? ''
                      }
                    />
                  </FormLabel>
                )}
            </div>
          )}

          {variant === 'edit-listing' &&
            !product.has_variations &&
            !product.is_auction && (
              <div className="flex w-full justify-between">
                <div className="flex w-full justify-between gap-4">
                  <div className="flex items-center gap-4 font-semibold">
                    <SaleFeature fill={'#C41719'} width={'40'} height={'40'} />
                    Sale Price
                  </div>
                  <Toggle
                    enabled={product.on_sale}
                    onChange={() => form.setValue('on_sale', !product.on_sale)}
                  />
                </div>
              </div>
            )}

          {(product.condition === 'New' || variant === 'edit-listing') &&
            !product.is_auction && (
              <div>
                <hr className="mb-8" />
                <FormLabel
                  value="Quantity in stock"
                  required={true}
                  className="mb-8"
                >
                  <div className="grow">
                    <FormCounter
                      value={product.stock ?? 1}
                      error={!!form.formState.errors?.stock}
                      onChange={(val) => {
                        const min = variant === 'edit-listing' ? 0 : 1;
                        const max = product.condition === 'New' ? 1000 : 1;
                        const value = Math.min(Math.max(min, val), max);
                        form.setValue('stock', value);
                      }}
                    />
                    {form.formState.errors?.stock && (
                      <p className="mt-[0.8rem] text-[1.3rem] text-brand-red">
                        {form.formState.errors?.stock.message}
                      </p>
                    )}
                  </div>
                </FormLabel>
              </div>
            )}

          {(userDoc?.roles?.reseller || userDoc?.roles?.support) &&
            product.stock <= 1 && (
              <>
                <span className="text-[1.6rem] text-brand-dark-gray">
                  If this item is part of a resale deal, please fill out the
                  following information:
                </span>
                <FormLabel value="Deal ID">
                  <FormSelect
                    name="purchase_entry_id"
                    allowEmpty={true}
                    error={!!form.formState.errors?.purchase_entry_id}
                    options={generateSelectOptions(
                      !!purchaseEntries
                        ? ['empty', ...purchaseEntries.map((entry) => entry.id)]
                        : [],
                      !!purchaseEntries
                        ? { ...purchaseEntryLabels, empty: 'No Deal ID' }
                        : {}
                    )}
                    control={form.control}
                    defaultValue={product?.purchase_entry_id}
                  />
                  {form.formState.errors?.purchase_entry_id && (
                    <p className="mt-[0.8rem] text-[1.3rem] text-brand-red">
                      {form.formState.errors?.purchase_entry_id.message}
                    </p>
                  )}
                </FormLabel>

                <FormLabel value="Purchase Price">
                  <Controller
                    control={form.control}
                    name={`purchase_entry_price`}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <div className="max-w-[20rem]">
                        <FormCurrencyInputFancy
                          placeholder={'0.00'}
                          error={!!form.formState.errors?.purchase_entry_price}
                          onChange={onChange}
                          onBlur={onBlur}
                          defaultValue={value?.toString()}
                        />
                      </div>
                    )}
                  />
                </FormLabel>
                {product.purchase_entry_id ===
                  BOOT_TRADE_IN_PURCHASE_ENTRY_ID && (
                  <FormLabel
                    value="Bought from"
                    errorMessage={form.formState.errors?.sources?.message}
                  >
                    <FormInput
                      value={boughtFrom ?? ''}
                      onChange={async (e) => {
                        setBoughtFrom(e.target.value);
                      }}
                      placeholder="Bought from"
                    />
                    {user && boughtFrom && (
                      <div className="my-4 flex items-center gap-4">
                        <p className="text-[1.6rem]">Found User:</p>
                        <SafeImage
                          src={user.photo ?? ''}
                          alt="User"
                          key={user.uid}
                          className="h-[5rem] w-[5rem] rounded-full"
                          width={50}
                          height={50}
                        />
                        <p className="text-[1.3rem]">{user.username}</p>
                      </div>
                    )}
                    {boughtFrom && (
                      <button
                        type="button"
                        className="text-underline text-[1.6rem]"
                        onClick={() => {
                          setBoughtFrom('');
                          form.setValue(
                            'sources',
                            product.sources?.filter(
                              (source) => source.collection !== 'boot_trade_in'
                            )
                          );
                          refetch();
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </FormLabel>
                )}
                {form.formState.errors?.purchase_entry_price && (
                  <p className="mt-[0.8rem] text-[1.3rem] text-brand-red">
                    {form.formState.errors?.purchase_entry_price.message}
                  </p>
                )}
              </>
            )}
        </div>

        <div className="mt-16 hidden w-fit flex-col gap-y-6 sm:mt-0 sm:flex">
          <h3 className="w-full text-[2rem] font-medium text-zinc-700">
            Set the best price for the product.
          </h3>

          <CheckItem text="Look at similar products on the marketplace" />
          <CheckItem text="Consider the demand for the product" />
          <CheckItem text="Regularly monitor prices and adjust accordingly to stay competitive" />
          <CheckItem text="Put sale price to gain more buyer attention" />
        </div>
      </div>
    </PageSection>
  );
};
